import React, { useId, useMemo } from "react";

/**
 * Sparkline — a minimalist SVG polyline with an optional soft fill under the
 * curve. No axes, no labels, just the trend shape. Used inside KPI cards.
 *
 * Props:
 *  - data: number[] (required)
 *  - color: string  (stroke + fill hue)
 *  - width, height: px
 *  - showFill: bool (adds a soft gradient under the line)
 *  - showDot: bool (highlights the last point)
 */
export const Sparkline = ({
  data = [],
  color = "#00B4D8",
  width = 120,
  height = 32,
  showFill = true,
  showDot = true,
  strokeWidth = 1.6,
}) => {
  const path = useMemo(() => {
    if (!data.length) return { line: "", area: "", last: null };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = data.length > 1 ? width / (data.length - 1) : width;
    const pad = 2;
    const y = (v) => pad + (1 - (v - min) / range) * (height - pad * 2);
    const pts = data.map((v, i) => [i * stepX, y(v)]);
    const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const area = `${line} L${width},${height} L0,${height} Z`;
    return { line, area, last: pts[pts.length - 1] };
  }, [data, width, height]);

  const rid = useId().replace(/:/g, "");
  if (!data.length) return null;
  const gid = `spark-${rid}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {showFill && <path d={path.area} fill={`url(#${gid})`} />}
      <path
        d={path.line}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showDot && path.last && (
        <circle cx={path.last[0]} cy={path.last[1]} r={2.4} fill={color} stroke="#fff" strokeWidth={1} />
      )}
    </svg>
  );
};

export default Sparkline;
